import type { QueryStatus, QueryObserverOptions } from '@tanstack/react-query';

import type { ApiResponse } from '@openclassrooms/sdk-web';

import type { Permission } from '../interfaces';
import getUseGet from './getUseGet';
import checkPermission from './checkPermission';

type GetListPermissionsHookParams<ApiResponseRootKey> = {
  entityName: string;
  apiResponseRootKey: ApiResponseRootKey;
  fetch: (params?: unknown) => Promise<ApiResponse>;
};

type PermissionItemKey = string | number | 'all';
type PermissionList = Record<
  PermissionItemKey,
  {
    permissions: Array<Permission>;
  }
>;

type GetListPermissionsHookReturn<PermissionCodes extends string, HookParams = unknown> = (
  params?: HookParams,
  options?: QueryObserverOptions
) => {
  isFetching: boolean;
  isLoading: boolean;
  isPending: boolean;
  initialStatus: QueryStatus;
  hasError: boolean;
  can: (
    permissionCode: PermissionCodes,
    itemId: PermissionItemKey,
    isAllUsedAsFallback?: boolean
  ) => boolean;
};

export default function getPermissionListHook<
  PermissionCodes extends string,
  ApiResponseRootKey extends string,
  HookParams = unknown
>({
  entityName,
  apiResponseRootKey,
  fetch,
}: GetListPermissionsHookParams<ApiResponseRootKey>): GetListPermissionsHookReturn<
  PermissionCodes,
  HookParams
> {
  const usePermissions = getUseGet(fetch, entityName);

  return (
    params?: HookParams,
    queryOptions?: Omit<QueryObserverOptions, 'queryKey' | 'queryFn'>
  ) => {
    const {
      isFetching,
      isPending,
      isLoading,
      data,
      error,
      status: initialStatus,
      refetch,
    } = usePermissions(params, queryOptions) as ReturnType<typeof usePermissions> & {
      data: Record<ApiResponseRootKey, PermissionList>;
    };

    const allPermissions: PermissionList = data ? (data[apiResponseRootKey] as PermissionList) : {};
    const hasPermissionsAboutAllItems = Boolean(allPermissions.all?.permissions?.length);
    const hasError = Boolean(error);

    const can = (
      permissionCode: PermissionCodes,
      itemId: PermissionItemKey,
      isAllUsedAsFallback?: boolean
    ) => {
      let isAllowed = false;

      if (hasPermissionsAboutAllItems) {
        isAllowed = checkPermission(permissionCode, allPermissions.all.permissions);
      }

      const hasPermissionsAboutSpecificItem = Boolean(allPermissions[itemId]?.permissions?.length);

      if (!hasPermissionsAboutSpecificItem) {
        return isAllowed;
      }

      if (isAllUsedAsFallback) {
        return checkPermission(permissionCode, allPermissions[itemId].permissions);
      }

      return isAllowed || checkPermission(permissionCode, allPermissions[itemId].permissions);
    };

    return {
      isFetching,
      isPending,
      isLoading,
      hasError,
      can,
      initialStatus,
      refetch,
    };
  };
}
